import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Calendar, Clock } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { CTASection } from "@/components/site/CTASection";
import { IMAGES } from "@/data/content";
import { api } from "@/lib/api";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export default function Blog() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => {
        if (r.data.length) setPosts(r.data);
      })
      .catch(() => {});
  }, []);

  const [featured, ...rest] = posts;

  const comingSoon = () =>
    toast("Full article coming soon", {
      description: "We're preparing this story for publication. Check back shortly.",
    });

  const linkHost = (url) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch (e) {
      return "the source";
    }
  };

  const FeaturedTag = featured?.link ? motion.a : motion.button;
  const featuredLinkProps = featured?.link
    ? { href: featured.link, target: "_blank", rel: "noopener noreferrer" }
    : { type: "button", onClick: comingSoon };

  return (
    <div data-testid="blog-page">
      <PageHeader
        eyebrow="Blog & Field Notes"
        title="From The Workshop Floor"
        subtitle="Aeromodelling tips, NCC events, defence project updates, and stories from our workshops with the Aeromodellers' Association India."
        image={IMAGES.cnc}
      />

      <section className="bg-aero-deep">
        <div className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
          {/* Featured */}
          {featured && (
          <FeaturedTag
            {...featuredLinkProps}
            data-testid="blog-featured"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="group grid w-full overflow-hidden rounded-2xl border border-steel/20 bg-aero-surface text-left lg:grid-cols-2"
          >
            <div className="relative aspect-[16/10] overflow-hidden lg:aspect-auto">
              <img
                src={featured.image}
                alt={featured.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute left-5 top-5 rounded-full bg-saffron px-3 py-1 font-heading text-[11px] font-semibold uppercase tracking-wide text-white">
                Featured
              </span>
            </div>
            <div className="flex flex-col justify-center p-8 sm:p-12">
              <span className="font-heading text-xs uppercase tracking-[0.22em] text-saffron">
                {featured.category}
              </span>
              <h2 className="mt-4 font-heading text-3xl font-bold leading-tight tracking-tight text-ink sm:text-4xl">
                {featured.title}
              </h2>
              <p className="mt-4 whitespace-pre-line font-body text-base leading-relaxed text-steel">
                {featured.excerpt}
              </p>
              <div className="mt-6 flex items-center gap-6 font-body text-xs text-steel">
                <span className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5" /> {formatDate(featured.date)}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5" /> {featured.readTime}
                </span>
              </div>
              <span data-testid="blog-featured-read-link" className="mt-6 inline-flex items-center gap-2 font-heading text-sm font-semibold text-saffron">
                {featured.link ? `Read the full article on ${linkHost(featured.link)}` : "Read Article"}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>
          </FeaturedTag>
          )}

          {/* Grid */}
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((post, i) => {
              const CardTag = post.link ? motion.a : motion.button;
              const cardProps = post.link
                ? { href: post.link, target: "_blank", rel: "noopener noreferrer" }
                : { type: "button", onClick: comingSoon };
              return (
              <CardTag
                {...cardProps}
                key={post.id}
                data-testid={`blog-card-${post.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="group flex flex-col overflow-hidden rounded-xl border border-steel/20 bg-aero-surface text-left transition-all duration-500 hover:-translate-y-1 hover:border-saffron/50"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full border border-saffron/40 bg-aero-deep/70 px-3 py-1 font-heading text-[10px] uppercase tracking-wide text-saffron backdrop-blur">
                    {post.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-heading text-lg font-semibold leading-snug text-ink">
                    {post.title}
                  </h3>
                  <p className="mt-3 flex-1 font-body text-sm leading-relaxed text-steel">
                    {post.excerpt}
                  </p>
                  <div className="mt-5 flex items-center justify-between border-t border-steel/20 pt-4 font-body text-xs text-steel">
                    <span>{formatDate(post.date)}</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </CardTag>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection
        title="Have a topic you'd like us to cover?"
        subtitle="Tips, build logs, competition recaps — tell us what you'd like to read next."
        primaryLabel="Suggest a Topic"
      />
    </div>
  );
}
